import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import api, { errMsg } from "../api";
import { Users, Loader2, MessageSquare, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

function isOnline(u) {
    if (!u?.last_seen) return false;
    return (Date.now() - new Date(u.last_seen).getTime()) / 1000 < 90;
}

function fmtLastSeen(iso, lang) {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return lang === "de" ? "jetzt" : "now";
    if (diff < 3600) return `${Math.floor(diff / 60)} ${lang === "de" ? "Min." : "min"}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${lang === "de" ? "Std." : "h"}`;
    return d.toLocaleDateString(LOCALES[lang]);
}

export default function EmployeesPage() {
    const { lang, user } = useAuth();
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const startChat = async (otherId) => {
        try {
            const { data } = await api.post("/chats", { member_ids: [otherId] });
            navigate(`/chat?chat=${data.id}`);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/employees");
            setEmployees(data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, [load]);

    const filtered = employees.filter((u) =>
        !filter ||
        u.name.toLowerCase().includes(filter.toLowerCase()) ||
        (u.job_title || "").toLowerCase().includes(filter.toLowerCase())
    );

    const onlineCount = employees.filter(isOnline).length;

    return (
        <div className="px-4 sm:px-8 py-8 max-w-6xl" data-testid="employees-page">
            <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-1.5">
                        <Users className="w-3 h-3" /> {t(lang, "employeeDirectory")}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-bold mt-1">{t(lang, "employeeDirectory")}</h1>
                    <p className="text-sm text-neutral-500 mt-1">
                        <span className="inline-flex items-center gap-1.5 mr-3">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                            <strong className="text-emerald-700">{onlineCount}</strong> {t(lang, "online")}
                        </span>
                        <span>{employees.length} {t(lang, "employees").toLowerCase()}</span>
                    </p>
                </div>
                <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                    <Input
                        placeholder={t(lang, "search")}
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="pl-9"
                        data-testid="employees-search"
                    />
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filtered.map((u) => {
                        const online = isOnline(u);
                        return (
                            <div
                                key={u.id}
                                data-testid={`employee-${u.id}`}
                                className="p-4 bg-white border border-neutral-200 rounded-lg hover:shadow-md hover:border-neutral-300 transition group"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="relative shrink-0">
                                        <div className="w-12 h-12 rounded-full bg-neutral-900 text-white font-semibold flex items-center justify-center">
                                            {u.name?.slice(0, 2)?.toUpperCase()}
                                        </div>
                                        <span
                                            data-testid={`emp-online-${u.id}`}
                                            title={online ? t(lang, "online") : `${t(lang, "lastSeen")}: ${fmtLastSeen(u.last_seen, lang)}`}
                                            className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${online ? "bg-emerald-500" : "bg-neutral-300"}`}
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm truncate">{u.name}</p>
                                        {u.job_title && <p className="text-xs text-neutral-600 truncate">{u.job_title}</p>}
                                        <p className={`text-[10px] mt-1 ${online ? "text-emerald-700 font-semibold" : "text-neutral-400"}`}>
                                            {online ? `● ${t(lang, "online")}` : `${t(lang, "lastSeen")}: ${fmtLastSeen(u.last_seen, lang)}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-neutral-100 flex items-center justify-between">
                                    <span className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">{u.role}</span>
                                    {u.id !== user?.id ? (
                                        <button
                                            onClick={() => startChat(u.id)}
                                            data-testid={`emp-msg-${u.id}`}
                                            className="text-xs flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-200 text-neutral-700 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition"
                                        >
                                            <MessageSquare className="w-3 h-3" />
                                            {t(lang, "sendMessage")}
                                        </button>
                                    ) : (
                                        <span className="text-xs text-neutral-400">{lang === "de" ? "(du)" : "(you)"}</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
